/**
 * GetAppButton — pill CTA that smart-routes to the right app store.
 *
 * - iOS / iPadOS Safari → App Store
 * - Android → Google Play
 * - Desktop / unknown → "Get the App" landing (App Store URL by default)
 * - Auto-hides when running INSIDE the native Capacitor app (no point
 *   asking users to "Get the App" when they ARE the app).
 *
 * Uses the BLK PACKS brand gradient (orange-amber-red) instead of the
 * reference's green to match the rest of the site.
 */
import React from "react";
import { ArrowRight } from "lucide-react";

const APP_STORE_URL = "https://apps.apple.com/app/id6760724806";
// TODO: replace once Google Play listing is live
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=fun.blkpacks.app";

function pickStoreUrl() {
  if (typeof navigator === "undefined") return APP_STORE_URL;
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return PLAY_STORE_URL;
  // iPad on iOS 13+ reports as Mac; check for touch
  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (ua.includes("Mac") && navigator.maxTouchPoints > 1);
  if (isIOS) return APP_STORE_URL;
  return APP_STORE_URL;
}

function isInsideNativeApp() {
  if (typeof window === "undefined") return false;
  // Capacitor sets these flags on the window object when running native
  const cap = window.Capacitor;
  if (cap && (cap.isNativePlatform?.() || cap.isNative)) return true;
  // Fallback: Capacitor injects this on iOS WebKit
  const ua = navigator.userAgent || "";
  if (/BLKPacksApp\//i.test(ua)) return true;
  return false;
}

const GetAppButton = ({
  size = "md", // "sm" | "md" | "lg"
  fullWidth = false,
  className = "",
  testId = "get-app-btn",
}) => {
  if (isInsideNativeApp()) return null;

  const sizeStyles = {
    sm: "text-[11px] px-3 py-1.5 gap-1.5",
    md: "text-[12px] px-4 py-2 gap-2",
    lg: "text-[13px] px-5 py-2.5 gap-2",
  }[size] || "text-[12px] px-4 py-2 gap-2";

  const url = pickStoreUrl();

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`inline-flex items-center justify-center whitespace-nowrap shrink-0 font-black uppercase tracking-[0.18em] text-black rounded-full brand-gradient-bg active:scale-[0.97] transition-all shadow-[0_6px_22px_-6px_rgba(249,115,22,0.55)] hover:shadow-[0_10px_28px_-6px_rgba(249,115,22,0.7)] ${sizeStyles} ${fullWidth ? "w-full" : ""} ${className}`}
      style={{ fontFamily: "'Oswald', 'Inter', system-ui, sans-serif" }}
    >
      Get the App
      <ArrowRight size={14} strokeWidth={3} />
    </a>
  );
};

export default GetAppButton;
